/**
 * Result types for the deterministic half of the coach (architecture §6 steps 1–4).
 * Everything in this file is produced without an LLM and is unit-testable.
 */
import { z } from 'zod';

/** Mirrors the `coach_reports.verdict` CHECK constraint in the init migration. */
export const VerdictSchema = z.enum([
  'insufficient_data',
  'overreaching',
  'deload_recommended',
  'progressing_fast',
  'progressing',
  'on_track',
  'stalled',
]);
export type Verdict = z.infer<typeof VerdictSchema>;

export const TrendDirectionSchema = z.enum([
  'improving',
  'flat',
  'declining',
  'insufficient_data',
]);
export type TrendDirection = z.infer<typeof TrendDirectionSchema>;

/** Aggregate numbers displayed in the report banner. */
export const EvaluationMetricsSchema = z.object({
  totalVolumeKg: z.number(),
  volumeChangePct: z.number(),
  avgRpe: z.number(),
  compliancePct: z.number(),
  sessionsAnalyzed: z.number().int(),
  prCount: z.number().int(),
  acuteChronicRatio: z.number(),
  weeksAnalyzed: z.number().int(),
});
export type EvaluationMetrics = z.infer<typeof EvaluationMetricsSchema>;

/** Per-exercise verdict inside the evaluation matrix. */
export const ExerciseInsightSchema = z.object({
  exerciseId: z.string(),
  exerciseName: z.string(),
  trend: TrendDirectionSchema,
  bestE1rmKg: z.number(),
  e1rmChangePct: z.number(),
  weeksAtCurrentLoad: z.number().int(),
  /** Machine-readable recommendation, e.g. `increase_load`, `hold_and_add_rep`. */
  recommendation: z.string(),
  reason: z.string(),
  /** Consecutive weeks with no improvement at the end of the window. */
  stallWeeks: z.number().int(),
  /** True when the movement is scored on top-set reps (bodyweight / unloaded). */
  bodyweight: z.boolean(),
  weeklyPoints: z.array(
    z.object({
      weekStart: z.string(),
      bestE1rmKg: z.number(),
      topWeightKg: z.number(),
      workingSets: z.number().int(),
    }),
  ),
});
export type ExerciseInsight = z.infer<typeof ExerciseInsightSchema>;

export const FatigueFlagCodeSchema = z.enum([
  'weekly_avg_rpe_high',
  'rpe_creep',
  'failed_set_rate_high',
  'volume_spike',
  'acute_chronic_ratio_high',
  'acute_chronic_ratio_low',
  'compliance_collapse',
]);
export type FatigueFlagCode = z.infer<typeof FatigueFlagCodeSchema>;

export const FatigueFlagSchema = z.object({
  code: FatigueFlagCodeSchema,
  severity: z.enum(['warning', 'critical']),
  message: z.string(),
  evidence: z.record(z.union([z.number(), z.string(), z.boolean(), z.null()])),
});
export type FatigueFlag = z.infer<typeof FatigueFlagSchema>;

export const EvaluationResultSchema = z.object({
  verdict: VerdictSchema,
  headline: z.string(),
  metrics: EvaluationMetricsSchema,
  exerciseInsights: z.array(ExerciseInsightSchema),
  fatigueFlags: z.array(FatigueFlagSchema),
  dataQuality: z.array(z.string()),
  /** Deterministic machine-readable explanation of how the verdict was reached. */
  verdictReasons: z.array(z.string()),
});
export type EvaluationResult = z.infer<typeof EvaluationResultSchema>;

/** Options for `evaluateBlock` — all injected so the engine is time-independent. */
export interface EvaluateOptions {
  /** ISO timestamp treated as "now"; defaults to the packet's `generatedAt`. */
  now?: string;
  /** Force a minimum window even if the packet is short (used by tests). */
  minSessions?: number;
  /** Minimum number of distinct training weeks required for a real verdict. */
  minWeeks?: number;
}

/** One field-level before/after inside a `PrescribedChange`. */
export const PrescribedChangeFieldSchema = z.object({
  field: z.enum(['sets', 'repMin', 'repMax', 'targetWeightKg', 'targetRpe']),
  before: z.number().nullable(),
  after: z.number().nullable(),
  unit: z.string(),
});
export type PrescribedChangeField = z.infer<typeof PrescribedChangeFieldSchema>;

/** A single `plan_exercises` row the coach wants to rewrite. */
export const PrescribedChangeSchema = z.object({
  planExerciseId: z.string(),
  exerciseId: z.string(),
  exerciseName: z.string(),
  dayId: z.string(),
  dayLabel: z.string(),
  action: z.enum(['increase_load', 'hold_and_add_rep', 'reduce_load', 'deload', 'blocked', 'no_change']),
  /** Machine-readable progression rule that fired, e.g. `double_progression.top_of_range`. */
  rule: z.string(),
  /** Human sentence in the brand voice (§6 of the brand book). */
  rationale: z.string(),
  fields: z.array(PrescribedChangeFieldSchema),
  /** Optional short note (e.g. the substitute movement for an injured one). */
  note: z.string().optional(),
});
export type PrescribedChange = z.infer<typeof PrescribedChangeSchema>;

export const PlanProposalSchema = z.object({
  planId: z.string().nullable(),
  planName: z.string().nullable(),
  /** Plan week that will be performed once this proposal is applied. */
  week: z.number().int(),
  changes: z.array(PrescribedChangeSchema),
  notes: z.array(z.string()),
  needsMoreData: z.boolean(),
  /** `evaluation.verdict` this proposal was generated from. */
  basedOnVerdict: VerdictSchema,
});
export type PlanProposal = z.infer<typeof PlanProposalSchema>;

export const SafetyRailCodeSchema = z.enum([
  'LOAD_INCREASE_CAPPED',
  'VOLUME_INCREASE_CAPPED',
  'VOLUME_DECREASE_CAPPED',
  'RPE_CLAMPED',
  'INJURY_MOVEMENT_BLOCKED',
  'DELOAD_OVERRIDES_INCREASE',
]);
export type SafetyRailCode = z.infer<typeof SafetyRailCodeSchema>;

export const SafetyRailSchema = z.object({
  code: SafetyRailCodeSchema,
  /** Plain-English explanation shown in the report and stored on `coach_reports`. */
  explanation: z.string(),
  /** Which exercise triggered the rail, when the rail is exercise-scoped. */
  exerciseName: z.string().optional(),
  /** Exact numbers behind the rail, for audit. */
  detail: z.string().optional(),
});
export type SafetyRail = z.infer<typeof SafetyRailSchema>;

export interface SafetyRailOutcome {
  proposal: PlanProposal;
  railsHit: SafetyRail[];
}

/** One ReAct step: Reason, Act, Observe — persisted to `agent_coaching_logs`. */
export const ReactStepSchema = z.object({
  index: z.number().int(),
  thought: z.string(),
  action: z.string(),
  actionInput: z.record(z.unknown()),
  observation: z.unknown(),
  tokensIn: z.number().int(),
  tokensOut: z.number().int(),
  latencyMs: z.number(),
  /** True when this step produced the accepted final report. */
  final: z.boolean().optional(),
  error: z.string().optional(),
});
export type ReactStep = z.infer<typeof ReactStepSchema>;

/** The validated `finalize_report` payload the LLM is allowed to write. */
export const CoachReportSchema = z.object({
  verdict: VerdictSchema,
  headline: z.string().min(1),
  summaryMd: z.string().min(1),
  nextWeekChecklist: z.array(z.string().min(1)).min(1),
  exerciseNotes: z
    .array(
      z.object({
        exerciseName: z.string().min(1),
        note: z.string().min(1),
      }),
    )
    .optional(),
});
export type CoachReport = z.infer<typeof CoachReportSchema>;

/** Everything one `runCoachReAct` call produces, before persistence. */
export interface CoachRun {
  runId: string;
  userId: string;
  startedAt: string;
  steps: ReactStep[];
  evaluation: EvaluationResult;
  proposal: PlanProposal;
  railsHit: SafetyRail[];
  report: CoachReport;
  /** True when the deterministic report replaced the LLM output (budget or schema failure). */
  fallbackUsed: boolean;
  /** Why the fallback fired, when it did. */
  fallbackReason?: string;
  model: string;
  tokensIn: number;
  tokensOut: number;
  latencyMs: number;
}
